import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Loader } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [heroWalk, setHeroWalk] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
  });

  // Fetch a random published walk for the left panel hero
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/walks?limit=5`);
        if (res.ok) {
          const data = await res.json();
          const walks = data.walks || [];
          if (walks.length > 0) {
            setHeroWalk(walks[Math.floor(Math.random() * walks.length)]);
          }
        }
      } catch { /* ignore */ }
    })();
  }, [isOpen]);

  // Reset when mode changes
  useEffect(() => { setMode(initialMode); }, [initialMode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setFormData({ email: '', password: '', displayName: '' });
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    const { error } = await signInWithGoogle();
    if (error) setError(error);
    else onClose();
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (mode === 'signup') {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters.');
        setLoading(false);
        return;
      }
      const { error } = await signUpWithEmail(formData.email, formData.password, formData.displayName);
      if (error) setError(error);
      else onClose();
    } else {
      const { error } = await signInWithEmail(formData.email, formData.password);
      if (error) setError(error);
      else onClose();
    }
    setLoading(false);
  };

  const heroImage = heroWalk?.main_image || heroWalk?.images?.[0] || null;
  const heroTitle = heroWalk?.title || '';
  const heroCategory = heroWalk?.categories?.[0] || '';
  const heroDistance = heroWalk?.distance_meters
    ? `${(heroWalk.distance_meters / 1609.34).toFixed(1)} mi`
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-0 overflow-hidden bg-white rounded-xl border-0 shadow-2xl sm:max-w-[860px] max-h-[90vh] [&>button]:hidden">
        <div className="flex min-h-[560px]">
          {/* ── Left panel ──────────────────────────── */}
          <div
            className="hidden md:flex flex-col justify-between relative overflow-hidden"
            style={{ width: '42%', minWidth: '42%', background: '#120a1e' }}
          >
            {/* Background: hero image or color bands */}
            {heroImage ? (
              <img
                src={heroImage}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0">
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(to bottom, #2d1854 0%, #7d3060 40%, #c4583a 70%, #120a1e 100%)',
                }} />
              </div>
            )}
            {/* Dark gradient overlay */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(18,10,30,0.3) 0%, rgba(18,10,30,0.1) 30%, rgba(18,10,30,0.6) 70%, rgba(18,10,30,0.95) 100%)',
            }} />

            {/* Top: Logo */}
            <div className="relative z-10 p-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8853D' }}>
                  <div className="w-4 h-4 border-2 border-white rounded-full" />
                </div>
                <span className="text-white font-bold text-sm tracking-wide">WANDERLOOP</span>
              </div>
            </div>

            {/* Bottom: Walk info */}
            <div className="relative z-10 p-5">
              {heroCategory && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E8853D' }}>
                    {heroCategory}
                  </span>
                  {heroDistance && (
                    <span className="text-[11px] text-white/50 px-2 py-0.5 rounded-full bg-white/10">
                      {heroDistance}
                    </span>
                  )}
                </div>
              )}
              {heroTitle && (
                <p className="text-white font-semibold text-lg leading-tight mb-2">{heroTitle}</p>
              )}
              <p className="text-white/50 text-xs">Your city looks different on foot.</p>
            </div>
          </div>

          {/* ── Right panel ─────────────────────────── */}
          <div className="flex-1 flex flex-col justify-center p-6 sm:p-8">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 rounded z-10"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Dots */}
            <div className="flex gap-1.5 mb-5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E8853D' }} />
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#C4607A' }} />
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7B5EA7' }} />
            </div>

            {/* Heading */}
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {mode === 'signin' ? "Welcome back. The city's waiting." : 'Create your account'}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {mode === 'signin'
                ? 'Sign in to continue your walks.'
                : 'Join Wanderloop to save walks and explore your city.'}
            </p>

            {/* Google */}
            <button
              onClick={handleGoogle}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 mb-4"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-gray-400">or</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === 'signup' && (
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  placeholder="First name"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                />
              )}
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Email"
                required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
              />
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#E8853D' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4762e'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#E8853D'}
              >
                {loading ? (
                  <><Loader className="w-4 h-4 animate-spin" /> Please wait...</>
                ) : (
                  mode === 'signin' ? 'Sign in' : 'Create account'
                )}
              </button>
            </form>

            {/* Switch */}
            <p className="text-center text-sm text-gray-500 mt-4">
              {mode === 'signin' ? (
                <>Don't have an account?{' '}
                  <button onClick={() => switchMode('signup')} className="font-medium hover:underline" style={{ color: '#E8853D' }}>
                    Sign up
                  </button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button onClick={() => switchMode('signin')} className="font-medium hover:underline" style={{ color: '#E8853D' }}>
                    Sign in
                  </button>
                </>
              )}
            </p>

            <p className="text-[11px] text-gray-400 text-center mt-4">
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
